import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaCircle, 
  FaClock, 
  FaCheckCircle, 
  FaTimesCircle, 
  FaBriefcase, 
  FaBuilding, 
  FaMapMarkerAlt,
  FaCalendar,
  FaPlay 
} from 'react-icons/fa';

const ApplicationHistory = ({ applications, jobs }) => {
  const navigate = useNavigate();
  
  const handleLogicalTest = (jobId, applicationId) => {
    console.log('Navigating to logical test:', jobId, applicationId);
    navigate(`/logical-test/${jobId}`, {
      state: { applicationId: applicationId }
    });
  };
  const getJobDetails = (jobId) => {
    return jobs.find(job => job.id === jobId) || {};
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'text-yellow-500 bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800';
      case 'accepted': return 'text-green-500 bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800';
      case 'rejected': return 'text-red-500 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800';
      default: return 'text-gray-500 bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <FaClock className="w-5 h-5" />;
      case 'accepted': return <FaCheckCircle className="w-5 h-5" />;
      case 'rejected': return <FaTimesCircle className="w-5 h-5" />;
      default: return <FaCircle className="w-5 h-5" />;
    }
  };

  return (
    <div className="max-w-5xl mx-auto">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-md p-8">
        <div className="flex items-center space-x-3 mb-8 pb-4 border-b border-gray-100 dark:border-gray-700">
          <FaBriefcase className="text-2xl text-indigo-600 dark:text-indigo-400" />
          <h2 className="text-2xl font-bold text-gray-800 dark:text-white">My Applications</h2>
        </div>
        
        {applications.length > 0 ? (
          <div className="space-y-6">
            {applications.map((application, index) => {
              const job = getJobDetails(application.jobId);
              const statusColor = getStatusColor(application.status);
              
              return (
                <div key={index} 
                     className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 
                              dark:border-gray-700 p-6 hover:shadow-lg transition-all duration-300">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4">
                      <div className="w-16 h-16 bg-gray-50 dark:bg-gray-700 rounded-xl p-3 
                                    border border-gray-100 dark:border-gray-600 flex items-center justify-center">
                        <img src={job.logo} alt={job.company} className="w-full h-full object-contain" />
                      </div>
                      
                      <div className="space-y-1">
                        <h3 className="font-bold text-xl text-gray-900 dark:text-white">{job.position}</h3>
                        <div className="flex items-center space-x-2 text-gray-600 dark:text-gray-300">
                          <FaBuilding className="text-gray-400 dark:text-gray-500" />
                          <span>{job.company}</span>
                        </div>
                        <div className="flex items-center space-x-2 text-gray-600 dark:text-gray-300">
                          <FaMapMarkerAlt className="text-gray-400 dark:text-gray-500" />
                          <span>{job.location}</span>
                        </div>
                      </div>
                    </div>

                    <div className={`px-4 py-2 rounded-full border ${statusColor} flex items-center space-x-2`}>
                      {getStatusIcon(application.status)}
                      <span className="font-medium capitalize">{application.status}</span>
                    </div>
                  </div>

                  <div className="mt-6 flex flex-wrap gap-3">
                    <span className="px-3 py-1 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 
                                   dark:text-indigo-300 rounded-full text-sm font-medium">
                      {job.salary}
                    </span>
                    <span className="px-3 py-1 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 
                                   dark:text-indigo-300 rounded-full text-sm font-medium">
                      {job.mode}
                    </span>
                    <span className="px-3 py-1 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 
                                   dark:text-indigo-300 rounded-full text-sm font-medium">
                      {job.role}
                    </span>
                  </div>

                  <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
                    {/* Timeline with connecting line */}
                    <div className="relative pl-6 before:content-[''] before:absolute before:left-2 
                         before:top-0 before:h-full before:w-0.5 before:bg-gray-200 dark:before:bg-gray-700">
                      
                      {/* Status Badge */}
                      <div className="flex items-center mb-3">
                        <div className="absolute left-0 w-4 h-4 rounded-full bg-indigo-500 border-2 border-white dark:border-gray-800"></div>
                        <div className={`ml-6 px-3 py-1 rounded-full text-sm ${getStatusColor(application.status)}`}>
            
                          <span className="ml-1  capitalize">{application.status}</span>
                        </div>
                      </div>

                      {/* ATS Score */}
                      {application.atsScore && (
                        <div className="flex items-center mb-3">
                          <div className="absolute left-0 w-4 h-4 rounded-full bg-green-500 border-2 border-white dark:border-gray-800"></div>
                          <div className={`ml-6 px-3 py-1 rounded-full text-sm ${
                            application.atsScore >= 70 ? 'bg-green-100 text-green-800' :
                            application.atsScore >= 50 ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'}`}>
                            ATS Score: {application.atsScore}%
                          </div>
                        </div>
                      )}

                      {/* Test Status */}
                      {application.testDate && (
                        <div className="flex items-center mb-3">
                          <div className="absolute left-0 w-4 h-4 rounded-full bg-blue-500 border-2 border-white dark:border-gray-800"></div>
                          <div className="ml-6 flex items-center justify-between w-full">
                            <div className="flex items-center gap-2">
                              <FaCalendar className="text-blue-500" />
                              <span className="text-sm">
                                Test: {new Date(application.testDate).toLocaleDateString()}
                              </span>
                            </div>
                            {application.testStatus ? (
                              <div className="flex items-center gap-3">
                                <span className={`px-3 py-1 rounded-full text-xs ${
                                  application.testStatus.passed ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                }`}>
                                  Score: {application.testStatus.score}/15
                                </span>
                                {/* Inside the Test Status section, update the button onClick */}
                                {application.testStatus.score >= 10 && !application.logicalTest && (
                                  <button
                                    onClick={() => handleLogicalTest(application.jobId, application.id)}
                                    className="px-4 py-1 bg-indigo-500 text-white text-xs rounded-full hover:bg-indigo-600 flex items-center gap-2"
                                  >
                                    <FaPlay className="text-xs" />
                                    Take Logical Test
                                  </button>
                                )}
                                {application.logicalTest && (
                                  <span className="px-3 py-1 bg-blue-50 text-blue-800 rounded-full text-xs">
                                    Logical: {application.logicalTest.score}/100
                                  </span>
                                )}
                              </div>
                            ) : (
                              

                              <button
                              onClick={() =>
                                navigate(`/aptitude-test/${application.jobId}`, {
                                  state: {
                                    companyName: application.companyName,
                                    jobId: application.jobId,
                                  },
                                })
                              }
                              className="px-4 py-1 bg-blue-500 text-white text-xs rounded-full hover:bg-blue-600 flex items-center gap-2"
                            >
                              Start Aptitude Test
                            </button>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Application Date */}
                      <div className="flex items-center">
                        <div className="absolute left-0 w-4 h-4 rounded-full bg-gray-400 border-2 border-white dark:border-gray-800"></div>
                        <span className="ml-6 text-xs text-gray-500">
                          Applied: {new Date(application.appliedAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                  </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12 bg-gray-50 dark:bg-gray-700/50 rounded-xl 
                         border border-gray-200 dark:border-gray-700">
            <FaBriefcase className="mx-auto text-4xl text-gray-400 dark:text-gray-500 mb-4" />
            <p className="text-gray-600 dark:text-gray-300 font-medium">No applications yet</p>
            <p className="text-gray-500 dark:text-gray-400 text-sm mt-2">
              Start applying for jobs to see your applications here
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApplicationHistory;